import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import AuthModal from './AuthModal';
import PasswordField from '../PasswordField';
import * as FormStyled from './AuthModal.form.style';
import { AUTH_VALIDATIONS } from './constants/validations';

/**
 * Login Modal
 *
 * Allows users to log in with their email and password.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} [props.onSuccess] - Callback when login is successful
 * @param {Function} [props.onSwitchToCreateAccount] - Callback to switch to create account modal
 * @param {Function} [props.onSwitchToForgotPassword] - Callback to switch to forgot password modal
 * @param {string} [props.redirectTo] - URL to redirect to after successful login
 * @returns {object} The LoginModal component
 */
function LoginModal({
  isOpen,
  onClose,
  onSuccess,
  onSwitchToCreateAccount,
  onSwitchToForgotPassword,
  redirectTo
}) {
  const [globalError, setGlobalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loginWithPassword } = useAuthentication();
  const captureException = useCaptureException();

  // Initialize form
  const formMethods = useForm({
    defaultValues: {
      email: '',
      password: ''
    },
    mode: 'onBlur'
  });

  const { handleSubmit, register, formState } = formMethods;
  const { errors } = formState;

  /**
   * Handle form submission
   */
  const onSubmit = useCallback(
    async (data) => {
      setIsSubmitting(true);
      setGlobalError(null);

      try {
        // Determine redirect URL - avoid returning to anonymous threads
        // If user is on a thread page and no explicit redirectTo is provided,
        // redirect to chat entry instead (anonymous threads can't be accessed by authenticated users)
        let targetRedirect = redirectTo;
        if (!targetRedirect) {
          const isOnThreadPage = window.location.pathname.includes('/thread/');
          targetRedirect = isOnThreadPage
            ? '/guide/chat'
            : window.location.pathname + window.location.search;
        }

        const result = await loginWithPassword({
          email: data.email,
          password: data.password,
          redirectTo: targetRedirect
        });

        // Check if login returned an error
        if (!result) {
          throw new Error('Login failed - no result returned');
        }

        if (result.error || result.code === 'access_denied') {
          throw new Error(result.error_description || result.description || 'Login failed');
        }

        // SUCCESS: If we reach here, login succeeded
        // The auth provider will handle the redirect, so we call success callbacks
        // Note: In most cases, the page will redirect before these callbacks execute
        if (onSuccess) {
          onSuccess({ email: data.email });
        }

        // Close modal (will only execute if redirect hasn't happened yet)
        onClose();
      } catch (error) {
        captureException(error, {
          level: 'warning',
          context: { action: 'login' }
        });

        // Handle specific error types with safe, user-friendly messages
        if (error.message) {
          setGlobalError('Unable to log in. Please check your email and password and try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [loginWithPassword, onSuccess, onClose, captureException, redirectTo]
  );

  /**
   * Handle form submission errors
   */
  const onSubmitError = useCallback(() => {
    setGlobalError('Please fix the errors above and try again.');
  }, []);

  /**
   * Handle switch to create account
   */
  const handleSwitchToCreateAccount = useCallback(
    (e) => {
      e.preventDefault();
      if (onSwitchToCreateAccount) {
        onSwitchToCreateAccount();
      }
    },
    [onSwitchToCreateAccount]
  );

  /**
   * Handle switch to forgot password
   */
  const handleSwitchToForgotPassword = useCallback(
    (e) => {
      e.preventDefault();
      if (onSwitchToForgotPassword) {
        onSwitchToForgotPassword();
      }
    },
    [onSwitchToForgotPassword]
  );

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <AuthModal isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <FormStyled.FormRoot
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
        >
          <FormStyled.FormStack>
            {/* Global Error */}
            {globalError && (
              <Box pb={2}>
                <Text color="red" size="sm">
                  {globalError}
                </Text>
              </Box>
            )}

            {/* Title Section */}
            <FormStyled.TitleContainer>
              <FormStyled.Title size="lg" weight="bold">
                Log in to continue to Velo.
              </FormStyled.Title>
              <FormStyled.Subtitle>
                Don&apos;t have an account?{' '}
                <FormStyled.SwitchLink
                  disabled={isSubmitting}
                  onClick={handleSwitchToCreateAccount}
                >
                  Create account
                </FormStyled.SwitchLink>
              </FormStyled.Subtitle>
            </FormStyled.TitleContainer>

            {/* Form Fields */}
            <FormStyled.FieldStack>
              <TextField
                {...register('email', AUTH_VALIDATIONS.email)}
                autoComplete="email"
                autoFocus
                disabled={isSubmitting}
                error={Boolean(errors.email)}
                fullWidth
                helperText={errors.email?.message}
                label="Email"
                required
                type="email"
              />

              <PasswordField
                {...register('password', AUTH_VALIDATIONS.currentPassword)}
                autoComplete="current-password"
                disabled={isSubmitting}
                error={Boolean(errors.password)}
                fullWidth
                helperText={errors.password?.message}
                label="Password"
                required
              />
            </FormStyled.FieldStack>

            {/* Forgot Password Link */}
            <Box>
              <FormStyled.SwitchLink disabled={isSubmitting} onClick={handleSwitchToForgotPassword}>
                Forgot password?
              </FormStyled.SwitchLink>
            </Box>

            {/* Buttons */}
            <FormStyled.ButtonStack>
              <FormStyled.SubmitButton
                disabled={hasErrors || isSubmitting}
                loading={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Logging in...' : 'Continue'}
              </FormStyled.SubmitButton>

              <FormStyled.SecondaryButton variant="outlined" onClick={onClose}>
                Close
              </FormStyled.SecondaryButton>
            </FormStyled.ButtonStack>
          </FormStyled.FormStack>
        </FormStyled.FormRoot>
      </FormProvider>
    </AuthModal>
  );
}

LoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onSwitchToCreateAccount: PropTypes.func,
  onSwitchToForgotPassword: PropTypes.func,
  redirectTo: PropTypes.string
};

export default LoginModal;
